import { useEffect, useMemo, useCallback } from 'react';
import { useParams, useLocation, useHistory, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';

import { trackEvent } from 'helpers/analytics';
import { useShareContactsMutation } from 'services/userV2';
import { userSelector } from 'redux/selectors';
import strings from 'strings';

const ALLY_ONBOARDING_START_TRACKED_KEY = 'ally_onboarding_start_tracked';

export const useAllySignup = () => {
  const user = useSelector(userSelector);
  const { networkSlug } = useParams();
  const network = user?.allyingCollections?.find((nc) => nc.slug === networkSlug);
  const { url: rawUrl } = useRouteMatch();
  const url = rawUrl.replace(/\/$/, '');
  const location = useLocation();
  const history = useHistory();
  const [shareContacts, { isLoading: isShareContactsLoading }] = useShareContactsMutation();

  const isShareContactsRoute = location.pathname.includes('/share-contacts');
  const isDoneStep = location.pathname.includes('/complete');

  // Determine current step based on route
  const onboardingSteps = useMemo(() => ['account', 'integrations', 'done'], []);

  const currentStepIndex = useMemo(() => {
    const currentPath = location.pathname;
    const isAccountStep = currentPath === url || currentPath.includes('/profile/');
    const isIntegrationsStep = currentPath.includes('/integrations') || currentPath.includes('/linkedin/');

    if (isDoneStep) return 2;
    if (isIntegrationsStep) return 1;
    if (isAccountStep) return 0;
    return 0;
  }, [location.pathname, url, isDoneStep]);

  // Track ally onboarding start - only when starting fresh (not completed yet)
  useEffect(() => {
    if (network?.id && !user.onboardingCompleted) {
      const trackedNetworkId = sessionStorage.getItem(ALLY_ONBOARDING_START_TRACKED_KEY);
      if (trackedNetworkId !== String(network.id)) {
        sessionStorage.setItem(ALLY_ONBOARDING_START_TRACKED_KEY, String(network.id));
        trackEvent('ally_onboarding:start', {
          collection_id: network.id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [network?.id]);

  // Steps for the sidebar
  const steps = useMemo(
    () => [
      {
        title: 'Sync your contacts (less than 2 minutes)',
        description: 'Connect LinkedIn and Google to keep your contacts automatically up to date.',
      },
      {
        title: `${network?.name} finds meaningful intros`,
        description: `The ${network?.name} team can search your contacts and send intro requests to you.`,
      },
      {
        title: 'Make the intro',
        description: `You choose which intros to make, helping both ${network?.name} and your contacts.`,
      },
    ],
    [network?.name],
  );

  // Show "Share My Contacts" prompt for returning allies who haven't shared contacts with this network yet
  const showShareContactsPrompt = user.onboardingCompleted && !network?.shareContacts;
  const isAtRootPath = location.pathname === url;

  // Determine redirect scenarios
  const shouldRedirectToStayTuned = !network;
  // Don't redirect to home if user is on the completion page
  const shouldRedirectToHome = user.onboardingCompleted && network?.shareContacts && !isDoneStep;
  const shouldRedirectToShareContacts = showShareContactsPrompt && isAtRootPath;
  const shareContactsRedirectUrl = `${url}/share-contacts`;

  // For returning allies clicking "Share my contacts", share and go to dashboard
  const handleShareContacts = useCallback(async () => {
    const { data, error } = await shareContacts({
      collectionId: network?.id,
      share: true,
    });

    if (error) {
      toast(strings.genericError, { icon: 'error', id: 'share-contacts' });
      return;
    }

    if (data) {
      history.push('/');
    }
  }, [shareContacts, network?.id, history]);

  return {
    user,
    network,
    url,
    onboardingSteps,
    currentStepIndex,
    steps,
    isShareContactsRoute,
    isShareContactsLoading,
    handleShareContacts,
    shouldRedirectToStayTuned,
    shouldRedirectToHome,
    shouldRedirectToShareContacts,
    shareContactsRedirectUrl,
  };
};
